import { useState } from 'react';
import Header from '../assessor-directory/components/Header';
import Footer from '../assessor-directory/components/Footer';
import { countyAssessors } from '../../mocks/county-assessors';

export default function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Get all states from the countyAssessors object keys
  const states = Object.keys(countyAssessors).sort();

  const handleSearch = () => {
    setHasSearched(true);
    const results: any[] = [];
    
    // Search through all states
    Object.entries(countyAssessors).forEach(([stateCode, counties]) => {
      // Filter by selected state if specified
      if (selectedState && stateCode !== selectedState) return;
      
      // Filter counties by search query
      counties.forEach(county => {
        const matchesQuery = searchQuery === '' || 
          county.county.toLowerCase().includes(searchQuery.toLowerCase()) ||
          county.assessorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          county.address.toLowerCase().includes(searchQuery.toLowerCase());
        
        if (matchesQuery) {
          results.push({
            ...county,
            state: stateCode
          });
        }
      });
    });
    
    setSearchResults(results);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-800 to-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Search Assessor Offices
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl">
            Find county assessor offices and appraisal districts by location. Search by county name, city, or state.
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-8 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Search Input */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Search by County or City
                </label>
                <div className="relative">
                  <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-xl"></i>
                  <input
                    type="text"
                    placeholder="Enter county or city name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full pl-12 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                  />
                </div>
              </div>

              {/* State Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Filter by State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base cursor-pointer"
                >
                  <option value="">All States</option>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={handleSearch}
              className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line mr-2"></i>
              Search
            </button>
          </div>

          {/* Results */}
          {hasSearched && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  Search Results
                  <span className="text-slate-400 ml-3 text-lg">({searchResults.length} found)</span>
                </h2>
              </div>

              {searchResults.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {searchResults.map((assessor, index) => (
                    <div
                      key={index}
                      className="bg-slate-800 border border-slate-700 rounded-lg p-6 hover:border-emerald-500 transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-1">
                            {assessor.county}
                          </h3>
                          <p className="text-sm text-slate-400">{assessor.state}</p>
                        </div>
                        <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg">
                          <i className="ri-government-line text-white"></i>
                        </div>
                      </div>

                      <p className="text-sm text-slate-300 mb-4">{assessor.assessorName}</p>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-start">
                          <i className="ri-map-pin-line text-emerald-400 mt-1 mr-2 flex-shrink-0"></i>
                          <p className="text-sm text-slate-300">{assessor.address}</p>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-phone-line text-emerald-400 mr-2 flex-shrink-0"></i>
                          <a href={`tel:${assessor.phone}`} className="text-sm text-slate-300 hover:text-emerald-400 transition-colors cursor-pointer">
                            {assessor.phone}
                          </a>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {assessor.services.slice(0, 3).map((service: string, serviceIndex: number) => (
                            <span
                              key={serviceIndex}
                              className="px-2 py-1 bg-slate-700 text-slate-300 rounded-full text-xs border border-slate-600"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>

                      <a
                        href={assessor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-external-link-line mr-2"></i>
                        Visit Website
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-800 border border-slate-700 rounded-lg">
                  <i className="ri-search-line text-6xl text-slate-600 mb-4"></i>
                  <p className="text-xl text-slate-400 mb-2">No results found</p>
                  <p className="text-slate-500">Try adjusting your search criteria</p>
                </div>
              )}
            </div>
          )}

          {!hasSearched && (
            <div className="text-center py-12">
              <i className="ri-search-2-line text-6xl text-slate-600 mb-4"></i>
              <p className="text-xl text-slate-400">Enter your search criteria above to find assessor offices</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
